import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type { Integration } from "@prescreen/lib/types"
import {
  DEFAULT_BANKING_CAPABILITIES,
  type BankingCapabilities,
  type BankingPermissionTier,
} from "@prescreen/lib/types"
import { Loader2 } from "lucide-react"
import { TooltipProvider } from "@/components/ui/tooltip"
import { SecretPermissionsForm } from "@/components/SecretPermissionsForm"
import {
  buildSecretGrantedScopesFromSelections,
  integrationHasConfigurableOAuthSecrets,
  secretFormSelectionsMeetMinimum,
} from "@/lib/oauth-secret-scope-builder"

export type AddIntegrationSecretDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: Integration
  onSubmit: (payload: {
    name: string
    description?: string
    grantedScopes: string[]
  }) => Promise<void>
  /** When parent is awaiting POST completion */
  isSubmitting?: boolean
}

export function AddIntegrationSecretDialog({
  open,
  onOpenChange,
  integration,
  onSubmit,
  isSubmitting = false,
}: AddIntegrationSecretDialogProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [capabilities, setCapabilities] = useState<BankingCapabilities>({
    ...DEFAULT_BANKING_CAPABILITIES,
  })
  const [synergyDocumentsTier, setSynergyDocumentsTier] =
    useState<BankingPermissionTier>("none")
  const [prequalTier, setPrequalTier] = useState<BankingPermissionTier>("none")
  const [hardpullTier, setHardpullTier] = useState<BankingPermissionTier>("none")
  const [submitting, setSubmitting] = useState(false)

  const hasConfigurableSecrets = integrationHasConfigurableOAuthSecrets(integration)

  useEffect(() => {
    if (!open) {
      return
    }
    setName("")
    setDescription("")
    setCapabilities({ ...DEFAULT_BANKING_CAPABILITIES })
    setSynergyDocumentsTier("none")
    setPrequalTier("none")
    setHardpullTier("none")
  }, [open, integration._id])

  const updateTier = (key: keyof BankingCapabilities, tier: BankingPermissionTier) => {
    const nextTier =
      key === "rdcRequests" && tier === "write" ? ("read" satisfies BankingPermissionTier) : tier
    setCapabilities((prev) => ({
      ...DEFAULT_BANKING_CAPABILITIES,
      ...prev,
      [key]: nextTier,
    }))
  }

  const scopesValid = secretFormSelectionsMeetMinimum(
    integration,
    capabilities,
    synergyDocumentsTier,
    prequalTier,
    hardpullTier,
  )

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed || !scopesValid) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        name: trimmed,
        description: description.trim() || undefined,
        grantedScopes: buildSecretGrantedScopesFromSelections(
          integration,
          capabilities,
          synergyDocumentsTier,
          prequalTier,
          hardpullTier,
        ),
      })
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const trimmedName = name.trim()
  const busy = submitting || isSubmitting
  const submitEnabled =
    Boolean(trimmedName) &&
    !busy &&
    hasConfigurableSecrets &&
    scopesValid

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Secret</DialogTitle>
          <DialogDescription>
            Name this credential and choose token permissions. Grant at least read access on one permission across the
            sections that apply to this integration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="secret-name">Secret name</Label>
            <Input
              id="secret-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production reporting"
              className="mt-1"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="secret-desc">Description (optional)</Label>
            <Textarea
              id="secret-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this credential is used for"
              className="mt-1 min-h-[72px]"
            />
          </div>

          {hasConfigurableSecrets ? (
            <TooltipProvider delayDuration={200}>
              <SecretPermissionsForm
                idPrefix="add-secret"
                integration={integration}
                bankingCapabilities={capabilities}
                onBankingTierChange={updateTier}
                synergyDocumentsTier={synergyDocumentsTier}
                onSynergyDocumentsTierChange={setSynergyDocumentsTier}
                prequalTier={prequalTier}
                onPrequalTierChange={setPrequalTier}
                hardpullTier={hardpullTier}
                onHardpullTierChange={setHardpullTier}
              />
            </TooltipProvider>
          ) : (
            <p className="text-sm text-muted-foreground">
              Configure Equifax prequalification, Equifax DIT, Jack Henry Silverlake, or Synergy on this integration
              before adding OAuth secrets.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" disabled={!submitEnabled} onClick={() => void handleSubmit()}>
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Creating…
              </>
            ) : (
              "Generate secret"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
